import { EditOutlined, PlusOutlined } from '@ant-design/icons';
import { Show } from '@pankod/refine-antd';
import { useParams } from '@pankod/refine-react-router-v6';
import {
  Button,
  Card,
  Col,
  Drawer,
  Dropdown,
  Form,
  Input,
  InputNumber,
  Menu,
  Row,
  Select,
  Space,
  Table,
  message,
  notification,
} from 'antd';
import { useWatch } from 'antd/lib/form/Form';
import TextArea from 'antd/lib/input/TextArea';
import React, { useEffect, useState } from 'react';
import { RiDeleteBin5Line } from 'react-icons/ri';
import {
  createSalesCopyDeliverySlip,
  getSalesCopyDeliverySlip,
  getSalesDeliverySlip,
  getSalesDeliverySticker,
} from 'services/delivery-slip';
import { getSale } from 'services/sale';
import { previewPdfFromBase64 } from 'utilities/shared';

type Params = {
  id: string;
};

export const DeliverySlipCreate: React.FC = () => {
  const { id: salesId } = useParams<Params>();
  const [deliverySlip, setDeliverySlip] = useState<any | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [form] = Form.useForm();
  const [drawerForm] = Form.useForm();
  const [products, setProducts] = useState<any[]>([]);
  const [isDrawerVisible, setIsDrawerVisible] = useState(false);
  const [productOptions, setProductOptions] = useState<any[]>([]);
  const [isPrintButtonLoading, setIsPrintButtonLoading] = useState<boolean>(false);
  const [editDrawerForm] = Form.useForm();
  const [isEditDrawerVisible, setIsEditDrawerVisible] = useState<boolean>(false);
  const [availableQuantity, setAvailableQuantity] = useState<number | null>(null);
  const [deliverySlipId, setDeliverySlipId] = useState<string | null>(null);
  const [isSubmittingDeliverySlip, setIsSubmittingDeliverySlip] = useState<boolean>(false);
  const numberOfBoxes = useWatch('numberOfBoxes', form);
  const [isDeliveryStickerLoading, setIsDeliveryStickerLoading] = useState<boolean>(false);
  const boxOptions = numberOfBoxes ? Array.from({ length: numberOfBoxes }, (_, i) => i + 1) : [];

  useEffect(() => {
    const fetchDeliverySlip = async () => {
      setIsLoading(true);
      try {
        if (salesId) {
          try {
            // Attempt to fetch delivery slip using the new API
            const deliverySlipData = await getSalesCopyDeliverySlip(salesId);
            setDeliverySlipId(deliverySlip?.id);
            if (deliverySlipData) {
              setDeliverySlip(deliverySlipData);
              form.setFieldsValue({
                clientName: deliverySlipData.sales?.client?.name || '',
                contactNumber: deliverySlipData?.contactNumber || '',
                shippingAddress: deliverySlipData.shippingAddress || '',
                deliveryPartner: deliverySlipData.deliveryPartner || '',
                numberOfBoxes: deliverySlipData.numberOfBoxes || 0,
                note: deliverySlipData.notes || '',
              });
              setProducts(deliverySlipData.products || []); // Ensure products is not undefined
            }

            const salesData = await getSale(salesId);
            setProductOptions(salesData.salesProductsCopy);
          } catch (err) {
            const data = await getSale(salesId);
            form.setFieldsValue({
              clientName: data.client.name,
              contactNumber: data.client.mobileNumber,
              shippingAddress: data.salesAddress,
              deliveryPartner: '',
              numberOfBoxes: 0,
              note: data.note,
            });
            setDeliverySlip(data);
            setProducts([]); // Set products to an empty array in fallback
            setProductOptions(data.salesProductsCopy);
            if (!data) {
              // Handle errors from the new API and fall back to the old API
              notification.warning({
                message: 'API Error',
                description: `Error fetching from getSalesCopyDeliverySlip: ${
                  err instanceof Error ? err.message : 'Unknown error'
                }. Falling back to the getSale API.`,
              });
            }
          }
        } else {
          notification.error({
            message: 'Sales ID Missing',
            description: 'Unable to fetch delivery slip without a sales ID.',
          });
        }
      } catch (err) {
        notification.error({
          message: 'Failed to Fetch Data',
          description: err instanceof Error ? err.message : 'Unknown Error',
        });
      }
      setIsLoading(false);
    };

    fetchDeliverySlip();
  }, [salesId, form, deliverySlip?.id]);

  const onFinish = async (values: any) => {
    // Calculate unique box numbers
    const uniqueBoxCount = new Set(products.map((product) => product.box)).size;

    // Check if the provided number of boxes matches the calculated unique box count
    if (values.numberOfBoxes !== uniqueBoxCount) {
      notification.error({
        message: 'Incorrect Package Number',
        description: `The number of boxes provided (${values.numberOfBoxes}) does not match. The correct number of boxes is (${uniqueBoxCount}). Please correct it.`,
      });
      return;
    }

    if (!products?.length) {
      notification.error({ message: 'Error', description: 'Please select all products' });
      return;
    }

    // Calculate total product quantity
    const totalProductQuantity =
      products.length && products?.reduce((total, product) => total + product.quantity, 0);

    // Calculate total deliverySlip salesProductsCopy quantity
    const totalSalesProductsQuantity = productOptions?.reduce(
      (total: any, product: any) => total + product.quantity,
      0
    );

    if (totalProductQuantity !== totalSalesProductsQuantity) {
      notification.error({
        message: 'Error',
        description: 'Selected product quantity does not match total sales product quantity.',
      });
      return;
    }

    const payload = {
      salesId,
      clientName: values.clientName,
      contactNumber: values.contactNumber,
      shippingAddress: values.shippingAddress,
      deliveryPartner: values.deliveryPartner,
      numberOfBoxes: values.numberOfBoxes,
      notes: values.note,
      productInfo: products.map((product: any) => {
        const productData = {
          box: product.box,
          price: product.price,
          productVariantId: product.productVariantId,
          productName: product.productName,
          quantity: product.quantity,
          sku: product.sku,
          total: product.quantity * product.price,
          variantName: product.variantName,
        };
        return productData;
      }),
    };

    try {
      setIsSubmittingDeliverySlip(true);
      const response = await createSalesCopyDeliverySlip(payload);
      setDeliverySlip(response?.createSalesCopyDeliverySlip);
      setProducts(response?.createSalesCopyDeliverySlip?.products || []);
      setDeliverySlipId(response.createSalesCopyDeliverySlip.id);
      notification.success({
        message: 'Success',
        description: 'Delivery slip saved successfully.',
      });
    } catch (err: any) {
      notification.error({
        message: 'Failed',
        description: err?.message,
      });
    } finally {
      setIsSubmittingDeliverySlip(false);
    }
  };

  const handleAddProductClick = () => {
    drawerForm.resetFields();
    setIsDrawerVisible(true);
  };

  const handleDrawerSubmit = () => {
    const values = drawerForm.getFieldsValue();
    let findProduct;
    const updatedProducts = [...products];

    if (deliverySlip?.salesProductsCopy) {
      findProduct = deliverySlip.salesProductsCopy.find((product: any) => {
        return product.productVariant.id === values.productId;
      });
    } else if (deliverySlip?.products) {
      findProduct = deliverySlip.products.find((product: any) => {
        return product.productVariantId === values.productId;
      });
    }

    if (findProduct) {
      // const availableQuantity = findProduct.quantity;

      if (availableQuantity !== null && values.quantity > availableQuantity) {
        message.error(`Entered quantity exceeds available quantity. ${availableQuantity}`);
        return;
      }

      // Find the index of a product with the same productVariantId and box number
      const existingProductIndex = updatedProducts.findIndex(
        (product) =>
          product.productVariantId === values.productId && product.box === values.boxNumber
      );

      if (existingProductIndex !== -1) {
        // Update the existing product's quantity and total
        const existingProduct = updatedProducts[existingProductIndex];
        const newQuantity = existingProduct.quantity + values.quantity;

        if (availableQuantity !== null && values.quantity > availableQuantity) {
          message.error(`Total quantity exceeds available stock: ${availableQuantity}`);
          return;
        }

        updatedProducts[existingProductIndex] = {
          ...existingProduct,
          quantity: newQuantity,
          total: newQuantity * existingProduct.price,
        };

        setProducts(updatedProducts);
        message.success('Product updated successfully.');
      } else {
        // Add the new product
        const newProduct = findProduct.productVariant
          ? {
              productVariantId: findProduct.productVariant?.id,
              box: values.boxNumber,
              price: findProduct.unitPrice,
              productName: findProduct.productVariant?.productCopy?.name,
              quantity: values.quantity,
              sku: findProduct.productVariant?.sku,
              total: values.quantity * findProduct.unitPrice,
              variantName: findProduct.productVariant?.name,
            }
          : {
              productVariantId: findProduct.productVariantId,
              box: values.boxNumber,
              price: findProduct.price,
              productName: findProduct.productName,
              quantity: values.quantity,
              sku: findProduct.sku,
              total: values.quantity * findProduct.price,
              variantName: findProduct.variantName,
            };

        setProducts([...products, newProduct]);
        message.success('Product added successfully.');
      }

      // Reset the form fields
      drawerForm.resetFields();
    } else {
      message.error('Please select a product.');
    }
  };

  const handleEditDrawerSubmit = () => {
    const values = editDrawerForm.getFieldsValue();
    const updatedProducts = [...products];
    let findProduct: any;

    if (deliverySlip?.salesProductsCopy) {
      findProduct = deliverySlip.salesProductsCopy.find((product: any) => {
        return product.productVariant.id === values.productId;
      });
    } else if (deliverySlip?.products) {
      findProduct = deliverySlip.products.find((product: any) => {
        return product.productVariantId === values.productId;
      });
    }

    if (findProduct) {
      // const availableQuantity = findProduct.quantity;
      if (availableQuantity !== null && values.quantity > availableQuantity) {
        message.error(`Entered quantity exceeds available quantity. ${availableQuantity}`);
        return;
      }
    }

    const previousProduct = updatedProducts.findIndex(
      (product) =>
        product.productVariantId === values.productId && product.box === values.previousBoxNumber
    );

    // Find the index of a product with the same productVariantId and box number
    const existingProductIndex = updatedProducts.findIndex(
      (product) => product.productVariantId === values.productId && product.box === values.boxNumber
    );

    if (values.boxNumber !== values.previousBoxNumber) {
      if (existingProductIndex === -1) {
        notification.error({
          message: 'Error',
          description: 'Product does not exist in the delivery slip.',
        });
        return;
      }
      updatedProducts[existingProductIndex] = {
        ...updatedProducts[existingProductIndex],
        quantity: updatedProducts[existingProductIndex].quantity + values.quantity,
        total:
          (updatedProducts[existingProductIndex].quantity + values.quantity) *
          updatedProducts[existingProductIndex].price,
      };

      updatedProducts.splice(previousProduct, 1);
    } else {
      // Merge the product quantities and update the total
      updatedProducts[existingProductIndex] = {
        ...updatedProducts[existingProductIndex],
        quantity: values.quantity,
        total: values.quantity * updatedProducts[existingProductIndex].price,
      };
    }
    notification.success({
      message: 'Success',
      description: 'Product quantity updated successfully.',
    });
    editDrawerForm.resetFields();
    setIsEditDrawerVisible(false);
    setProducts(updatedProducts);
  };

  const handleProductSelect = (productId: string, quantity?: number) => {
    const selectedProduct = productOptions.find(
      (productOption: any) => productOption.productVariant.id === productId
    );

    if (selectedProduct) {
      const netQuantity = calculateNetQuantity(products, selectedProduct);
      const totalQuantity = netQuantity + (quantity ?? 0);
      setAvailableQuantity(totalQuantity); // Update the availableQuantity state
    } else {
      setAvailableQuantity(null); // Reset if no product is selected
    }
  };

  const handleProductChange = (productId: string) => {
    const selectedProductOption = productOptions.find(
      (option: any) => option.productVariant.id === productId
    );

    if (selectedProductOption) {
      const netQuantity = calculateNetQuantity(products, selectedProductOption);
      setAvailableQuantity(netQuantity);
    } else {
      setAvailableQuantity(null);
    }
  };

  const handleEditClick = (record: any, index: any) => {
    handleProductSelect(record.productVariantId, record.quantity);
    setIsEditDrawerVisible(true);
    editDrawerForm.setFieldsValue({
      productId: record.productVariantId,
      boxNumber: record.box,
      quantity: record.quantity,
      previousBoxNumber: record.box,
    });
  };

  const generatePrintSlip = async () => {
    setIsPrintButtonLoading(true);
    try {
      if (deliverySlipId) {
        const invoiceString = await getSalesDeliverySlip(deliverySlipId);
        previewPdfFromBase64(invoiceString);
      } else {
        notification.error({
          message: 'Failed',
          description: 'Delivery slip ID is missing.',
        });
      }
    } catch (error: any) {
      notification.error({
        message: 'Failed',
        description: error.message,
      });
    }
    setIsPrintButtonLoading(false);
  };

  const calculateNetQuantity = (products: any[], productOption: any): number => {
    const totalSelectedQuantity = products
      .filter((product: any) => product.productVariantId === productOption.productVariant.id)
      .reduce((sum: number, product: any) => sum + product.quantity, 0);

    return (productOption.quantity || 0) - totalSelectedQuantity;
  };

  const handleDeliveryStickerClick = async ({ key: language }: { key: string }) => {
    setIsDeliveryStickerLoading(true);
    try {
      if (deliverySlipId) {
        const printStickerString = await getSalesDeliverySticker(
          deliverySlipId,
          language as 'english' | 'bangla'
        );
        previewPdfFromBase64(printStickerString);
      } else {
        notification.error({
          message: 'Failed',
          description: 'Delivery slip ID is missing.',
        });
      }
    } catch (error: any) {
      notification.error({
        message: 'Failed',
        description: error.message,
      });
    }
    setIsDeliveryStickerLoading(false);
  };

  const deliveryStickerMoreMenu = (
    <Menu onClick={handleDeliveryStickerClick}>
      <Menu.Item key={'english'}>English</Menu.Item>
      <Menu.Item key={'bangla'}>Bangla</Menu.Item>
    </Menu>
  );

  const pageHeaders = (
    <>
      <Button
        name="Print Delivery Slip"
        loading={isPrintButtonLoading}
        onClick={() => generatePrintSlip()}
        disabled={products?.length === 0 && deliverySlip?.id}
      >
        Delivery Print Slip
      </Button>
      <Dropdown
        overlay={deliveryStickerMoreMenu}
        trigger={['click']}
        disabled={(products?.length === 0 && deliverySlip?.id) || isDeliveryStickerLoading}
      >
        <Button loading={isDeliveryStickerLoading}>Delivery Sticker</Button>
      </Dropdown>
    </>
  );

  const columns = [
    { title: 'Package #', dataIndex: 'box', key: 'box' },
    { title: 'SKU', dataIndex: 'sku', key: 'sku' },
    { title: 'Variant Name', dataIndex: 'variantName', key: 'variantName' },
    { title: 'Product Name', dataIndex: 'productName', key: 'productName' },
    { title: 'Quantity', dataIndex: 'quantity', key: 'quantity' },
    { title: 'Unit Price', dataIndex: 'price', key: 'price' },
    {
      title: 'Total',
      dataIndex: 'total',
      key: 'total',
      render: (_: any, record: any) => record.quantity * record?.price,
    },
    {
      title: 'Actions',
      dataIndex: 'actions',
      render: (_: any, record: any, index: any) => {
        return (
          <Space style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEditClick(record, index)}
            />
            <Button
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              danger
              size="small"
              icon={<RiDeleteBin5Line />}
              onClick={() => {
                const newProducts = products.filter(
                  (product) => product.sku !== record.sku || product.box !== record.box
                );
                setProducts(newProducts);
              }}
            />
          </Space>
        );
      },
    },
  ];

  return (
    <Show title="Deliver Slip" isLoading={isLoading} headerProps={{ extra: pageHeaders }}>
      <Form form={form} layout="vertical" onFinish={onFinish}>
        <Row justify="space-around" align="middle">
          <Col xs={24} lg={12}>
            <Card title="Recipient Information">
              <Form.Item
                label="Client Name"
                name="clientName"
                rules={[{ required: true, message: 'Please enter client name' }]}
              >
                <Input disabled />
              </Form.Item>
              <Form.Item
                label="Contact Number"
                name="contactNumber"
                rules={[{ required: true, message: 'Please enter contact number' }]}
              >
                <Input />
              </Form.Item>
              <Form.Item
                label="Shipping Address"
                name="shippingAddress"
                rules={[{ required: true, message: 'Please enter shipping address' }]}
              >
                <Input />
              </Form.Item>
              <Form.Item
                label="Delivery Partner"
                name="deliveryPartner"
                rules={[{ required: true, message: 'Please enter delivery partner name' }]}
              >
                <Input />
              </Form.Item>
              <Form.Item
                label="Number of Packages"
                name="numberOfBoxes"
                rules={[{ required: true, message: 'Please select number of boxes' }]}
              >
                <InputNumber style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item label="Note" name="note">
                <TextArea />
              </Form.Item>
            </Card>
          </Col>
        </Row>
        <div>
          <Space
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginTop: 24,
              marginBottom: 24,
            }}
          >
            <h2>Add Product</h2>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAddProductClick}>
              Add Product
            </Button>
          </Space>
          <div style={{ border: '1px solid #DEEEF8', borderRadius: '4px', overflow: 'hidden' }}>
            <Table
              columns={columns}
              dataSource={products.map((product: any) => ({
                ...product,
                key: `${product.sku}-${product.box}`, // Combine SKU and Box to create a unique key
              }))}
              pagination={false}
            />
          </div>
        </div>
        <Space style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 24 }}>
          <Button type="primary" htmlType="submit" loading={isSubmittingDeliverySlip}>
            Save
          </Button>
        </Space>
      </Form>
      <Drawer
        title="Add Product"
        placement="right"
        width={400}
        onClose={() => setIsDrawerVisible(false)}
        visible={isDrawerVisible}
      >
        <Form layout="vertical" form={drawerForm} onFinish={handleDrawerSubmit}>
          <Form.Item
            name="productId"
            label="Product"
            rules={[{ required: true, message: 'Please select a product' }]}
          >
            <Select
              placeholder="Select a product"
              options={
                Array.isArray(productOptions)
                  ? productOptions.map((productOption: any, index: React.Key) => {
                      // Aggregate quantities for the same productVariantId in the products array
                      const netQuantity = calculateNetQuantity(products, productOption);

                      return {
                        key: index,
                        value: productOption.productVariant.id,
                        label: `${productOption.productVariant.name} (${netQuantity})`,
                        disabled: netQuantity <= 0, // Disable if net quantity is 0 or less
                      };
                    })
                  : []
              }
              onChange={handleProductChange} // Trigger when a product is selected
            />
          </Form.Item>

          {/* Display the Product Quantity field dynamically */}
          {availableQuantity !== null && (
            <Form.Item label="Available Quantity" hidden>
              <InputNumber
                value={availableQuantity}
                readOnly
                style={{ width: '100%', backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
              />
            </Form.Item>
          )}

          {/* <Form.Item
            name="boxNumber"
            label="Box Number"
            rules={[{ required: true, message: 'Please enter box number' }]}
          >
            <InputNumber style={{ width: '100%' }} placeholder="Enter box number" />
          </Form.Item> */}
          <Form.Item
            name="boxNumber"
            label="Package Number"
            rules={[{ required: true, message: 'Please select a Package number' }]}
          >
            <Select
              placeholder="Select a Package number"
              options={boxOptions.map((box) => ({
                value: box,
                label: `Package ${box}`,
              }))}
              disabled={!boxOptions.length} // Disable dropdown if no boxes are selected
            />
          </Form.Item>
          <Form.Item
            name="quantity"
            label="Quantity"
            rules={[
              { required: true, message: 'Please enter a quantity' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  const selectedProductId = getFieldValue('productId');
                  const selectedProductOption = productOptions.find(
                    (option: any) => option.productVariant.id === selectedProductId
                  );

                  if (!selectedProductOption) {
                    return Promise.reject(new Error('Please select a valid product.'));
                  }

                  const netQuantity = calculateNetQuantity(products, selectedProductOption);

                  if (value > netQuantity) {
                    return Promise.reject(
                      new Error(`Quantity exceeds available stock. Maximum: ${netQuantity}`)
                    );
                  }

                  return Promise.resolve();
                },
              }),
            ]}
          >
            <InputNumber style={{ width: '100%' }} />
          </Form.Item>
          <Space style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 24 }}>
            <Button onClick={() => setIsDrawerVisible(false)}>Cancel</Button>
            <Button type="primary" htmlType="submit">
              Add
            </Button>
          </Space>
        </Form>
      </Drawer>

      <Drawer
        title="Edit Product"
        placement="right"
        width={400}
        onClose={() => setIsEditDrawerVisible(false)}
        visible={isEditDrawerVisible}
      >
        <Form layout="vertical" form={editDrawerForm} onFinish={handleEditDrawerSubmit}>
          <Form.Item name="productId" label="Product">
            <Select
              disabled
              placeholder="Select a product"
              options={
                Array.isArray(productOptions)
                  ? productOptions.map((productOption: any, index: React.Key) => {
                      // Aggregate quantities for the same productVariantId in the products array
                      const netQuantity = calculateNetQuantity(products, productOption);

                      return {
                        key: index,
                        value: productOption.productVariant.id,
                        label: `${productOption.productVariant.name} (${availableQuantity ?? 0})`,
                        disabled: netQuantity <= 0, // Disable if net quantity is 0 or less
                      };
                    })
                  : []
              }
            />
          </Form.Item>

          {/* Display the Product Quantity field dynamically */}
          {availableQuantity !== null && (
            <Form.Item label="Available Quantity" hidden>
              <InputNumber
                value={availableQuantity}
                readOnly
                style={{ width: '100%', backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
              />
            </Form.Item>
          )}

          <Form.Item
            hidden
            name="previousBoxNumber"
            label="Previous Package Number"
            rules={[{ required: true, message: 'Please enter Package number' }]}
          >
            <InputNumber disabled style={{ width: '100%' }} placeholder="Enter Package number" />
          </Form.Item>
          {/* <Form.Item
            name="boxNumber"
            label="Box Number"
            rules={[{ required: true, message: 'Please enter box number' }]}
          >
            <InputNumber style={{ width: '100%' }} placeholder="Enter box number" />
          </Form.Item> */}
          <Form.Item
            name="boxNumber"
            label="Package Number"
            rules={[{ required: true, message: 'Please select a Package number' }]}
          >
            <Select
              placeholder="Select a Package number"
              options={boxOptions.map((box) => ({
                value: box,
                label: `Package ${box}`,
              }))}
              disabled={!boxOptions.length} // Disable dropdown if no boxes are selected
            />
          </Form.Item>

          <Form.Item
            name="quantity"
            label="Quantity"
            rules={[{ required: true, message: 'Please enter quantity' }]}
          >
            <InputNumber style={{ width: '100%' }} placeholder="Enter quantity" />
          </Form.Item>

          <Space style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 24 }}>
            <Button onClick={() => setIsEditDrawerVisible(false)}>Cancel</Button>
            <Button type="primary" htmlType="submit">
              Save
            </Button>
          </Space>
        </Form>
      </Drawer>
    </Show>
  );
};
